import React, { useState, useEffect, useRef, useCallback } from 'react';
import AwesomeSlider from 'react-awesome-slider';
import withAutoplay from 'react-awesome-slider/dist/autoplay';
import { ReactSortable } from 'react-sortablejs';
import 'react-awesome-slider/dist/styles.css';
import 'react-awesome-slider/dist/custom-animations/cube-animation.css';
import 'react-awesome-slider/dist/custom-animations/fall-animation.css';
import 'react-awesome-slider/dist/custom-animations/fold-out-animation.css';
import 'react-awesome-slider/dist/custom-animations/open-animation.css';
import 'react-awesome-slider/dist/custom-animations/scale-out-animation.css';

import axios from 'axios';
import { toast } from 'react-toastify';

import './presentation.scss';


import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';

import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import PauseIcon from '@mui/icons-material/Pause';
import CloseIcon from '@mui/icons-material/Close';

import BrokenImageIcon from '@mui/icons-material/BrokenImage';

import AddIcon from '@mui/icons-material/Add';

import Helmet from 'react-helmet';

// NEW: Import the ImagePickerModal
import ImagePickerModal from './ImagePickerModal'; // Adjust path if necessary

const AutoplaySlider = withAutoplay(AwesomeSlider);

const Presentation = () => {
    const API_BASE_URL =
        process.env.NODE_ENV === 'production'
            ? process.env.REACT_APP_API_URL_PRODUCTION
            : process.env.REACT_APP_API_URL;

    const animations = [
        'openAnimation',
        'scaleOutAnimation',
    ];

    const [presentations, setPresentations] = useState([]);
    const [selected, setSelected] = useState(null);
    const [form, setForm] = useState({ title: '', photos: [], musicUrls: [''] });
    const [editing, setEditing] = useState(null);
    const [isFormOpen, setIsFormOpen] = useState(false);

    const [isPlaying, setIsPlaying] = useState(true);
    const [isMuted, setIsMuted] = useState(true);
    const [ytReady, setYtReady] = useState(false);
    const [showControls, setShowControls] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isLocked, setIsLocked] = useState(false);

    const playerRef = useRef(null);
    const idleTimer = useRef(null);
    const sliderRef = useRef(null);

    // Image Picker Modal State
    const [isImagePickerOpen, setIsImagePickerOpen] = useState(false);

    const [photoErrors, setPhotoErrors] = useState({});
    const [photoInputError, setPhotoInputError] = useState('');

    const photoInputRef = useRef(null);

    const [tempPhotos, setTempPhotos] = useState([]); // This will hold the photos for editing form

    // Keep track of the categories for filtering in ImagePickerModal (optional, but good practice)
    const [imageCategories, setImageCategories] = useState([]);


    useEffect(() => {
        fetchData(); // ✅ Load presentations on initial mount

        if (!window.YT) {
            const tag = document.createElement('script');
            tag.src = 'https://www.youtube.com/iframe_api'; // Correct YouTube Iframe API URL
            document.body.appendChild(tag);
            window.onYouTubeIframeAPIReady = () => setYtReady(true);
        } else {
            setYtReady(true);
        }
    }, []);

    const fetchData = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/presentations`);
            setPresentations(res.data);
        } catch {
            toast.error('Failed to fetch presentations');
        }
    };

    // NEW: Fetch categories for the image picker (if not already fetched by Images component)
    const fetchImageCategories = useCallback(async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/image-categories`);
            setImageCategories(res.data || []);
        } catch (err) {
            console.error('Error fetching image categories:', err);
            // Optionally toast.error here if it's critical
        }
    }, []);

    useEffect(() => {
        fetchImageCategories();
    }, [fetchImageCategories]);


    const handleSubmit = async (e) => {
        e.preventDefault();

        // Sort tempPhotos by order
        const sorted = [...tempPhotos].sort((a, b) => a.order - b.order);

        // Validation
        if (!form.title.trim() || sorted.length === 0) {
            toast.error('Missing fields: Title and at least one photo are required.');
            return;
        }

        // Normalize photo data
        const photos = sorted.map((p, index) => ({
            url: typeof p === 'string' ? p.trim() : p.url.trim(),
            order: index,
        }));

        // Clean music URLs
        const musicUrls =
            form.musicUrls?.map((url) => url.trim()).filter(Boolean) || [];

        const payload = {
            ...form,
            photos,
            musicUrls,
        };

        try {
            if (editing) {
                await axios.put(`${API_BASE_URL}/api/presentations/${editing._id}`, payload);
                toast.success('Updated successfully!');
            } else {
                await axios.post(`${API_BASE_URL}/api/presentations`, payload);
                toast.success('Created successfully!');
            }

            fetchData();

            // Reset form states
            setForm({ title: '', photos: [], musicUrls: [''] });
            setTempPhotos([]);
            setEditing(null);
            setIsFormOpen(false);
        } catch (err) {
            console.error('Save failed:', err.response?.data || err.message);
            toast.error('Save failed. Please check your inputs.');
        }
    };


    const handleEdit = (p) => {
        const cleanPhotos = (p.photos || []).map((photo, i) => {
            if (typeof photo === 'string') {
                return { url: photo.trim(), order: i };
            }
            if (photo && typeof photo.url === 'string') {
                return {
                    url: photo.url.trim(),
                    order: typeof photo.order === 'number' ? photo.order : i,
                };
            }
            return { url: '', order: i };
        }).filter(photo => photo.url); // Filter out any photos without a valid URL

        setEditing(p);

        setForm({
            title: p.title,
            photos: cleanPhotos,
            musicUrls: Array.isArray(p.musicUrls) ? p.musicUrls : [''], // Ensure at least one empty string for input
        });

        setTempPhotos(cleanPhotos); // ✅ Add this right after setForm

        setIsFormOpen(true);
    };

    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteId, setDeleteId] = useState(null);

    const confirmDelete = (id) => {
        setDeleteId(id); // Show modal
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        setIsDeleting(true);
        try {
            await axios.delete(`${API_BASE_URL}/api/presentations/${deleteId}`);
            toast.success('Deleted successfully!');
            fetchData(); // Reload data
        } catch {
            toast.error('Delete failed');
        } finally {
            setIsDeleting(false);
            setDeleteId(null); // Hide modal
        }
    };

    // No longer directly updating form.photos for adding/removing, use tempPhotos
    const handleAddPhoto = () => {
        const url = photoInputRef.current?.value?.trim();

        if (!url) {
            setPhotoInputError('Please enter a URL first');
            return;
        }

        const isValidUrl = /^https?:\/\/.+\.(jpg|jpeg|png|webp|gif|svg|bmp|tiff)(\?.*)?$/i.test(url);

        if (!isValidUrl) {
            setPhotoInputError('Invalid image URL');
            return;
        }

        const isDuplicate = tempPhotos.some(photo => photo.url === url);
        if (isDuplicate) {
            setPhotoInputError('Image already added');
            return;
        }

        setTempPhotos(prev => [
            ...prev,
            { url, order: prev.length },
        ]);

        setPhotoInputError('');
        photoInputRef.current.value = '';
    };


    const removePhotoField = (indexToRemove) => {
        const updated = tempPhotos.filter((_, i) => i !== indexToRemove);

        // Reorder after removal
        const reordered = updated.map((photo, i) => ({
            ...photo,
            order: i,
        }));

        setTempPhotos(reordered);
        // Reset selected photo if it was removed
        if (selectedPhotoIndex === indexToRemove) {
            setSelectedPhotoIndex(null);
        } else if (selectedPhotoIndex > indexToRemove) {
            setSelectedPhotoIndex(prev => prev - 1); // Adjust index if a photo before it was removed
        }
    };


    ///GET-YOUTUBE
    const extractYouTubeID = (url) => {
        const match = url.match(/(?:youtube\.com\/.*[?&]v=|youtu\.be\/)([^"&?/\\s]{11})/);
        return match ? match[1] : null;
    };

    const fetchYouTubeTitle = async (url) => {
        try {
            // Note: Directly accessing oembed with YouTube might have CORS issues or rate limits.
            // A proxy or backend endpoint might be needed for production.
            const oembedUrl = `https://www.youtube.com/oembed?url=${encodeURIComponent(url)}&format=json`;
            const res = await fetch(oembedUrl);
            const data = await res.json();
            return data.title;
        } catch {
            return 'Unknown Title';
        }
    };

    const [videoTitles, setVideoTitles] = useState({});


    useEffect(() => {
        const loadTitles = async () => {
            const titles = {};
            for (let url of form.musicUrls) {
                if (
                    url &&
                    typeof url === 'string' &&
                    url.trim() !== '' &&
                    /^https?:\/\/(www\.)?(youtube\.com|youtu\.be)\//.test(url) &&
                    !videoTitles[url]
                ) {
                    const title = await fetchYouTubeTitle(url);
                    titles[url] = title;
                }
            }
            setVideoTitles((prev) => ({ ...prev, ...titles }));
        };

        loadTitles();
    }, [form.musicUrls, videoTitles]); // Add videoTitles to dependency array to re-run when titles change


    useEffect(() => {
        if (!ytReady || !selected || !Array.isArray(selected.musicUrls)) return;

        const videoIds = selected.musicUrls.map(extractYouTubeID).filter(Boolean);
        if (!videoIds.length) return;

        let currentIndex = 0;

        const onPlayerReady = (event) => {
            const player = event.target;
            if (isMuted) player.mute();
            else player.unMute();
            player.playVideo();
        };

        const onPlayerStateChange = (event) => {
            const player = event.target;
            if (event.data === window.YT.PlayerState.ENDED) {
                currentIndex = (currentIndex + 1) % videoIds.length;
                player.loadVideoById(videoIds[currentIndex]);
            }
        };

        // Destroy existing player if any
        if (playerRef.current?.destroy) {
            playerRef.current.destroy();
        }

        // Create new player
        playerRef.current = new window.YT.Player('yt-player', {
            height: '0',
            width: '0',
            videoId: videoIds[currentIndex],
            playerVars: {
                autoplay: 1,
                controls: 0,
                mute: isMuted ? 1 : 0,
            },
            events: {
                onReady: onPlayerReady,
                onStateChange: onPlayerStateChange,
            },
        });

        // Cleanup function for useEffect
        return () => {
            if (playerRef.current && playerRef.current.destroy) {
                playerRef.current.destroy();
                playerRef.current = null;
            }
        };
    }, [ytReady, selected, isMuted]);




    const handleFullscreenToggle = () => {
        const el = sliderRef.current;
        if (!document.fullscreenElement) {
            el?.requestFullscreen?.();
            setIsFullscreen(true);
        } else {
            document.exitFullscreen?.();
            setIsFullscreen(false);
        }
    };

    useEffect(() => {
        const onFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener('fullscreenchange', onFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
    }, []);

    const handleActivity = () => {
        setShowControls(true);
        if (!isLocked) {
            clearTimeout(idleTimer.current);
            idleTimer.current = setTimeout(() => setShowControls(false), 2000);
        }
    };

    useEffect(() => {
        const handleKeydown = (e) => {
            if (e.key === 'Escape') setSelected(null);
            handleActivity();
        };

        window.addEventListener('mousemove', handleActivity);
        window.addEventListener('keydown', handleKeydown);
        window.addEventListener('touchstart', handleActivity);
        handleActivity();

        return () => {
            window.removeEventListener('mousemove', handleActivity);
            window.removeEventListener('keydown', handleKeydown);
            window.removeEventListener('touchstart', handleActivity);
            clearTimeout(idleTimer.current);
        };
    }, [isLocked]);


    // DRAG AND DROP
    const [draggedIndex, setDraggedIndex] = useState(null);

    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
    };

    const handleDragOver = (e, index) => {
        e.preventDefault(); // Required to allow drop
    };

    const handleDrop = (e, dropIndex) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === dropIndex) return;

        const updatedPhotos = [...tempPhotos];
        const draggedItem = updatedPhotos.splice(draggedIndex, 1)[0];
        updatedPhotos.splice(dropIndex, 0, draggedItem);

        // Reassign order values based on new positions
        const reordered = updatedPhotos.map((photo, i) => ({
            ...photo,
            order: i,
        }));

        setTempPhotos(reordered);
        setDraggedIndex(null);
    };




    //////////////photos

    const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);

    useEffect(() => {
        // When form.photos changes (e.g., on edit), update tempPhotos
        setTempPhotos([...form.photos]);
    }, [form.photos]);



    const movePhoto = (index, direction) => {
        // Ensure tempPhotos is properly ordered before manipulation
        const sorted = [...tempPhotos].sort((a, b) => a.order - b.order);
        const targetIndex = index + direction;

        if (targetIndex < 0 || targetIndex >= sorted.length) return;

        // Create a copy to modify
        const newSorted = [...sorted];
        const [movedPhoto] = newSorted.splice(index, 1);
        newSorted.splice(targetIndex, 0, movedPhoto);

        // Reassign order values based on new positions
        const reordered = newSorted.map((photo, i) => ({
            ...photo,
            order: i,
        }));

        setTempPhotos(reordered);
        setSelectedPhotoIndex(targetIndex); // Keep selected photo highlighted

        // ✅ Auto-scroll to new selected photo
        setTimeout(() => {
            document
                .getElementById(`photo-${targetIndex}`)
                ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }, 50);
    };


    //// scroll
    const photoScrollRef = useRef(null);

    const scrollSlider = (direction) => {
        if (photoScrollRef.current) {
            photoScrollRef.current.scrollBy({
                left: direction === 'left' ? -300 : 300,
                behavior: 'smooth',
            });
        }
    };


    // NEW: Handler for receiving selected images from ImagePickerModal
    const handleSelectedImagesFromPicker = (selectedImageUrls) => {
        const newPhotosToAdd = [];
        const existingPhotoUrls = new Set(tempPhotos.map(p => p.url));

        selectedImageUrls.forEach(url => {
            // Only add if not already in tempPhotos
            if (!existingPhotoUrls.has(url)) {
                newPhotosToAdd.push({ url, order: tempPhotos.length + newPhotosToAdd.length });
            }
        });

        if (newPhotosToAdd.length > 0) {
            setTempPhotos(prev => [...prev, ...newPhotosToAdd]);
            toast.success(`${newPhotosToAdd.length} image(s) added from gallery!`);
        } else {
            toast.info('No new images selected or all selected images already exist.');
        }

        setIsImagePickerOpen(false); // Close the image picker modal
    };



    //////startreturn
    return (
        <div className="presentation-wrapper">

            <Helmet>
                <title>NavarroJalen | Presentation</title>
                <meta name="description" content="NavarroJalen | Presentation" />
                <meta property="og:title" content="NavarroJalen | Presentation" />
            </Helmet>

            <button className="add-button" onClick={() => {
                setIsFormOpen(true);
                setEditing(null); // Ensure we are in "add" mode
                setForm({ title: '', photos: [], musicUrls: [''] }); // Reset form
                setTempPhotos([]); // Reset temp photos
            }}> <AddIcon /> </button>

            <div className="presentations-list">

                {presentations.map((p) => (

                    <div key={p._id} className="presentation-item">
                        <div className="presentation-item-row-1">
                            <div className="presentation-item-title">
                                {p.title}
                            </div>
                            <div className="presentation-item-length">
                                ({p.photos.length})
                            </div>
                        </div>

                        <div className='presentation-actions-div'>

                            <div className='presentation-actions-button' onClick={() => {
                                const randomAnim = animations[Math.floor(Math.random() * animations.length)];
                                setSelected({ ...p, animation: randomAnim });
                                setIsPlaying(true);
                                setIsMuted(true);
                            }}>
                                <PlayArrowIcon className='presentation-actions-icon' />
                            </div>

                            <div className='presentation-actions-button' onClick={() => handleEdit(p)}>
                                <EditIcon className='presentation-actions-icon' />
                            </div>

                            <div className='presentation-actions-button' onClick={() => confirmDelete(p._id)}>
                                <DeleteIcon className='presentation-actions-icon' />
                            </div>

                            <div className='presentation-actions-button' onClick={() => {
                                const baseURL = window.location.origin;
                                const safeTitle = encodeURIComponent(p.title.trim().toLowerCase().replace(/\s+/g, '-'));
                                const shareUrl = `${baseURL}/videos/public/${safeTitle}`; // This might be a placeholder, adjust to your public presentation URL
                                navigator.clipboard.writeText(shareUrl)
                                    .then(() => toast.success('Link copied to clipboard!'))
                                    .catch(() => toast.error('Failed to copy link.'));
                            }}>
                                <ContentCopyIcon className='presentation-actions-icon' />
                            </div>

                        </div>

                    </div>
                ))}


            </div>


            {/* ADD / EDIT MODAL */}
            {isFormOpen && (
                <div className="presentation-modal-overlay" onClick={() => setIsFormOpen(false)}>
                    <div className="presentation-modal" onClick={(e) => e.stopPropagation()}>
                        <h3>{editing ? 'Edit' : 'New'} Presentation</h3>
                        <form onSubmit={handleSubmit}>
                            <label>Title</label>

                            <input
                                value={form.title}
                                onChange={(e) => setForm({ ...form, title: e.target.value })}
                                required
                            />


                            {/* // ADD PHOTO URLs */}
                            <label>Photos</label>

                            <div className="photo-slider-container">
                                <div className="photo-slider-scroll" ref={photoScrollRef}>
                                    <div className="photo-slider">

                                        {/* //////////////////////////////SHOW-PHOTOS */}
                                        <div className="photo-slider-container">
                                            <div className="photo-slider-scroll" ref={photoScrollRef}>
                                                <div className="photo-slider">
                                                    {tempPhotos
                                                        .sort((a, b) => a.order - b.order)
                                                        .map((photo, index) => {
                                                            const url = photo?.url || '';
                                                            return (
                                                                <div
                                                                    key={`photo-${url}-${index}`}
                                                                    className={`photo-card ${selectedPhotoIndex === index ? 'selected' : ''}`}
                                                                    id={`photo-${index}`}
                                                                    onClick={() => {
                                                                        setSelectedPhotoIndex(index);
                                                                        setTimeout(() => {
                                                                            document
                                                                                .getElementById(`photo-${index}`)
                                                                                ?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                                                                        }, 50);
                                                                    }}
                                                                >
                                                                    {url ? (
                                                                        !photoErrors[index] ? (
                                                                            <img
                                                                                src={url}
                                                                                alt={`Photo ${index + 1}`}
                                                                                onError={() =>
                                                                                    setPhotoErrors((prev) => ({ ...prev, [index]: true }))
                                                                                }
                                                                            />
                                                                        ) : (
                                                                            <div className="photo-fallback">
                                                                                <BrokenImageIcon />
                                                                                <p className="photo-error-text">Image failed to load</p>
                                                                            </div>
                                                                        )
                                                                    ) : null}


                                                                    {url && (
                                                                        <button
                                                                            type="button"
                                                                            className="remove-btn"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation(); // prevent click selecting photo
                                                                                removePhotoField(index);
                                                                                setPhotoErrors((prev) => {
                                                                                    const copy = { ...prev };
                                                                                    delete copy[index];
                                                                                    return copy;
                                                                                });
                                                                                setSelectedPhotoIndex(null);
                                                                            }}
                                                                        >
                                                                            ❌
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>
                            {/* //////////////////////////////SHOW-PHOTOS */}


                            {/* BUTTON TO SCROLL TO THE LEFT */}

                            <div className="photo-slider-buttons-div">

                                <div className="scroll-button scroll-left">
                                    <button type="button" onClick={() => scrollSlider('left')}>◀</button>
                                </div>


                                {/* ////////////////////////////////////////EXTERNAL CONTROLS */}
                                {selectedPhotoIndex !== null && tempPhotos.length > 0 && (
                                    <div className="photo-external-controls">
                                        <button
                                            type="button" // ✅ Prevents accidental form submit
                                            onClick={() => movePhoto(selectedPhotoIndex, -1)}
                                            disabled={selectedPhotoIndex === 0}
                                        >
                                            ◀
                                        </button>

                                        <span>
                                            Order: {tempPhotos[selectedPhotoIndex]?.order + 1}
                                        </span>

                                        <button
                                            type="button" // ✅ Prevents accidental form submit
                                            onClick={() => movePhoto(selectedPhotoIndex, 1)}
                                            disabled={selectedPhotoIndex === tempPhotos.length - 1}
                                        >
                                            ▶
                                        </button>
                                    </div>
                                )}

                                {/* ////////////////////////////////////////EXTERNAL CONTROLS */}
                                <div className="scroll-button scroll-right">
                                    <button type="button" onClick={() => scrollSlider('right')}>▶</button>
                                </div>


                            </div>


                            <div className="photo-input-bottom">
                                <input
                                    type="text"
                                    ref={photoInputRef}
                                    placeholder="Paste image URL and press Add Photo"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            handleAddPhoto(); // call the same handler on Enter
                                        }
                                    }}
                                />


                                {/* 👇 Error message */}
                                {photoInputError && (
                                    <div className="error-message">{photoInputError}</div>
                                )}
                            </div>



                            {/* // ADD PHOTO URLs */}


                            <button type="button" onClick={handleAddPhoto}>+ Add Photo</button>
                            {/* NEW: Open Image Picker Modal Button */}
                            <button
                                type="button"
                                onClick={() => {
                                    if (!form.title.trim()) {
                                        toast.warn('Please enter a presentation title first.');
                                        return;
                                    }
                                    setIsImagePickerOpen(true);
                                }}
                            >
                                + Open NJ Gallery
                            </button>



                            <label className='youtube-url-title'>YouTube Music URLs</label>

                            <ReactSortable
                                list={form.musicUrls}
                                setList={(newList) => setForm((prev) => ({ ...prev, musicUrls: newList }))}
                                animation={150}
                            >
                                {form.musicUrls.map((url, index) => {
                                    const videoId = extractYouTubeID(url);
                                    const thumbnail = videoId
                                        ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` // Correct thumbnail URL
                                        : null;
                                    const title = videoTitles[url];

                                    return (
                                        <div key={url + index} className="music-url-field">

                                            {/* You can re-enable thumbnail if desired */}
                                            {/* {thumbnail && (
                                                <div className="yt-thumbnail">
                                                    <a
                                                        href={`https://www.youtube.com/watch?v=${videoId}`} // Correct video URL
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        <img src={thumbnail} alt="YouTube thumbnail" />
                                                    </a>
                                                </div>
                                            )} */}

                                            <div className="yt-input-section">
                                                <input
                                                    type="text"
                                                    value={url}
                                                    onChange={(e) => {
                                                        const updated = [...form.musicUrls];
                                                        updated[index] = e.target.value;
                                                        setForm({ ...form, musicUrls: updated });

                                                        // Reset title when URL changes
                                                        setVideoTitles((prev) => {
                                                            const copy = { ...prev };
                                                            delete copy[url];
                                                            return copy;
                                                        });
                                                    }}
                                                    placeholder="Paste YouTube URL"
                                                />

                                                {title && (
                                                    <div className="yt-title" title={title}>
                                                        {title}
                                                    </div>
                                                )}
                                            </div>

                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const updated = form.musicUrls.filter((_, i) => i !== index);
                                                    setForm({ ...form, musicUrls: updated });
                                                }}
                                            >
                                                ❌
                                            </button>
                                        </div>
                                    );
                                })}
                            </ReactSortable>



                            <button type="button" onClick={() => setForm({ ...form, musicUrls: [...form.musicUrls, ''] })}>
                                + Add YouTube URL
                            </button>


                            <div className="form-buttons">
                                <button type="submit" className="btn-submit">{editing ? 'Update' : 'Create'}</button>
                                <button
                                    type="button"
                                    className="btn-cancel"
                                    onClick={() => {
                                        setEditing(null);
                                        setForm({ title: '', photos: [], musicUrls: [''] });
                                        setTempPhotos([]);
                                        setIsFormOpen(false);
                                    }}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

            )}
            {/* * ADD / EDIT MODAL * */}


            {/* SLIDER MODAL */}
            {selected && (
                <div className={`slider-modal ${!showControls ? 'controls-hidden' : ''}`} ref={sliderRef}>
                    <div className="slider-content">
                        <AutoplaySlider
                            className="custom-progress awssld"
                            animation="foldOutAnimation"
                            play={isPlaying}
                            cancelOnInteraction={false}
                            interval={5000}
                            bullets={false}
                            organicArrows={true}
                        >
                            {(selected.photos || [])
                                .map((photo, i) => {
                                    if (typeof photo === 'string') {
                                        return { url: photo, order: i };
                                    } else if (photo && typeof photo.url === 'string') {
                                        return {
                                            url: photo.url,
                                            order: typeof photo.order === 'number' ? photo.order : i,
                                        };
                                    } else {
                                        return { url: '', order: i };
                                    }
                                })
                                .filter((p) => p.url)
                                .sort((a, b) => a.order - b.order)
                                .map((photo, i) => (
                                    <div key={`slider-${photo.url}-${photo.order ?? i}`}>
                                        <img src={photo.url} alt={`slide-${i}`} className="slide-image" />
                                    </div>
                                ))}


                        </AutoplaySlider>

                        {ytReady && Array.isArray(selected.musicUrls) && selected.musicUrls.length > 0 && (
                            <div style={{ display: 'none' }}>
                                <div id="yt-player" />
                            </div>
                        )}

                        {isMuted && (
                            <button
                                className="mute-indicator"
                                onClick={() => {
                                    setIsMuted(false);
                                    playerRef.current?.unMute?.();
                                }}
                            >
                                <VolumeOffIcon />
                            </button>
                        )}

                        <div className="slider-controls">
                            {!isMuted && (
                                <button
                                    onClick={() => {
                                        setIsMuted(true);
                                        playerRef.current?.mute?.();
                                    }}
                                >
                                    <VolumeUpIcon />
                                </button>
                            )}

                            <button onClick={() => setIsLocked(prev => !prev)} title={isLocked ? 'Unlock Controls' : 'Lock Controls'}>
                                {isLocked ? <LockIcon /> : <LockOpenIcon />}
                            </button>

                            <button
                                onClick={() => {
                                    const newPlay = !isPlaying;
                                    setIsPlaying(newPlay);
                                    if (playerRef.current) {
                                        newPlay ? playerRef.current.playVideo?.() : playerRef.current.pauseVideo?.();
                                    }
                                }}
                            >
                                {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                            </button>

                            <button onClick={handleFullscreenToggle} title={isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}>
                                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                            </button>

                            <button className="btn-close" onClick={() => setSelected(null)}>
                                <CloseIcon />
                            </button>
                        </div>


                    </div>
                </div>
            )}
            {/* SLIDER MODAL */}


            {/* NEW: Image Picker Modal Integration */}
            <ImagePickerModal
                isOpen={isImagePickerOpen}
                onClose={() => setIsImagePickerOpen(false)}
                onSelectImages={handleSelectedImagesFromPicker}
                presentationTitle={form.title.trim() || 'Untitled Presentation'} // Pass the current presentation title for auto-categorization
            />
            {/* End NEW: Image Picker Modal Integration */}


            {/* DELETE MODAL */}

            {deleteId && (
                <div className="modalOverlay">
                    <div className="modalAlertDiv">
                        <div className="modalTitle">Confirm Delete</div>

                        <p className="modalText">
                            Are you sure you want to delete this presentation?
                        </p>

                        <div className="modalButtonDiv">
                            <button
                                onClick={handleDelete}
                                disabled={isDeleting}
                                className="modalButton modalSubmitButton"
                            >
                                {isDeleting ? 'Deleting...' : 'Delete'}
                            </button>
                            <button
                                onClick={() => setDeleteId(null)}
                                disabled={isDeleting}
                                className="modalButton modalCancelButton"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}


            {/* DELETE MODAL */}



        </div>
    );
};

export default Presentation;